/**
 * Nachrichten-Posteingang im Dashboard - AUSSCHLIESSLICH sichtbar/relevant
 * für den Login "fratellis" (siehe Dashboard.tsx, isSkullDevils), da
 * derzeit nur dieser Benutzername als Empfänger vorkommt (siehe
 * backend/src/routes/messages.ts, RECIPIENT). Lesbar und löschbar, kein
 * Versand von hier aus (siehe stattdessen pages/messages/MessagesPage.tsx
 * für President/VizePresident/Phantom).
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Card, CardHeader } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type { DashboardMessage } from "../../types";

export default function DashboardInbox() {
  const [messages, setMessages] = useState<DashboardMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return api.get<DashboardMessage[]>("/api/messages/inbox").then(setMessages).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(message: DashboardMessage) {
    try {
      await api.delete(`/api/messages/inbox/${message.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  // Solange (noch) geladen wird oder gar keine Nachrichten vorliegen, wird
  // dieser Bereich komplett ausgeblendet, statt eine leere Karte zu zeigen -
  // das Dashboard soll nicht dauerhaft eine "keine Nachrichten"-Karte
  // belegen, sondern nur bei tatsächlich vorhandenen Nachrichten auftauchen.
  if (!loading && messages.length === 0) return null;

  return (
    <Card>
      <CardHeader title="✉️ Nachrichten" subtitle="Neue Nachrichten für dich" />
      {loading ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <div key={message.id} className="bg-surface-raised rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-gray-200 whitespace-pre-wrap break-words min-w-0">{message.content}</p>
                <button className="text-danger text-xs hover:underline shrink-0" onClick={() => handleDelete(message)}>Löschen</button>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-surface-border/60">
                <span className="text-xs text-gray-500">{message.created_by_name ?? "—"}</span>
                <span className="text-xs text-gray-500">{formatDateTime(message.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
